<script setup lang="ts">
// Главная лендинга: шапка, hero, «как работает», возможности, CTA, футер.
// Тексты — в src/i18n/{ru,uz,en}.ts (vue-i18n); иконки/номера шагов — здесь.
// Вход и регистрация — один путь: APP_URL + '/login' (вход только через Google,
// аккаунт создаётся автоматически при первом входе).
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { messages, setLang, SUPPORTED_LANGS, type Lang } from '@/i18n'

// Без VITE_APP_URL кабинет выводится из домена: прод (hubario.com) →
// app.hubario.com, иначе локальный core-local. Прод-сборка в CI переменную
// не передаёт — fallback обязан быть верным для прод-домена. При SSR-пререндере
// window нет — запекается прод-URL (пререндер только в прод-сборке).
const APP_URL =
  import.meta.env.VITE_APP_URL ||
  (typeof window === 'undefined' || window.location.hostname.endsWith('hubario.com')
    ? 'https://app.hubario.com'
    : 'http://app.hubario.local')
const LOGIN_URL = APP_URL + '/login'

const { t, locale } = useI18n()

/** Активный словарь (типизированный доступ к массивам steps/features). */
const dict = computed(() => messages[locale.value as Lang])

/** Шаги «как работает» (номер — из индекса). */
const steps = computed(() => dict.value.how.steps.map((s, i) => ({ ...s, n: String(i + 1) })))

/** SVG-иконки возможностей — порядок соответствует features.items словаря. */
const FEATURE_ICONS = [
  'M4 5h16v11H7l-3 3V5z',
  'M12 3v10m0 0 4-4m-4 4-4-4M5 21h14',
  'M12 4 3 9l9 5 9-5-9-5zm-9 9 9 5 9-5',
  'M12 8v5l3 3M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z',
  'M9 3v2m6-2v2M4 8h16M6 5h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2z',
  'M12 3 4 7v5c0 5 3.4 8.4 8 9 4.6-.6 8-4 8-9V7l-8-4z',
]

/** Ключевые возможности (текст из словаря + иконка по индексу). */
const features = computed(() =>
  dict.value.features.items.map((f, i) => ({ ...f, icon: FEATURE_ICONS[i] ?? '' })),
)
</script>

<template>
  <div class="page">
    <!-- Шапка -->
    <header class="header">
      <div class="container header-in">
        <a class="brand" href="/">
          <span class="brand-logo">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M21.5 4.5 2.9 11.7c-.8.3-.8 1.4.1 1.7l4.6 1.4 1.8 5.6c.3.8 1.3.9 1.8.2l2.6-3.4 4.8 3.5c.7.5 1.6.1 1.8-.7l3-14.3c.2-.9-.7-1.6-1.9-1.2z" />
            </svg>
          </span>
          <span class="brand-name">Hubario</span>
        </a>
        <nav class="nav">
          <a href="#how">{{ t('nav.how') }}</a>
          <a href="#features">{{ t('nav.features') }}</a>
        </nav>
        <div class="header-cta">
          <!-- Пилюля выбора языка: RU | UZ | EN -->
          <div class="lang-pill" role="group" aria-label="Language">
            <button
              v-for="lang in SUPPORTED_LANGS"
              :key="lang"
              type="button"
              class="lang-pill__seg"
              :class="{ 'is-active': locale === lang }"
              @click="setLang(lang)"
            >
              {{ lang.toUpperCase() }}
            </button>
          </div>
          <a class="btn btn-ghost" :href="LOGIN_URL">{{ t('header.login') }}</a>
          <a class="btn btn-primary" :href="LOGIN_URL">{{ t('header.start') }}</a>
        </div>
      </div>
    </header>

    <!-- Hero -->
    <section class="hero">
      <div class="container hero-in">
        <span class="hero-badge">{{ t('hero.badge') }}</span>
        <h1 class="hero-title">
          {{ t('hero.title1') }}<br />
          <span class="hero-grad">{{ t('hero.title2') }}</span>
        </h1>
        <p class="hero-sub">{{ t('hero.sub') }}</p>
        <div class="hero-cta">
          <a class="btn btn-primary btn-lg" :href="LOGIN_URL">
            {{ t('hero.ctaPrimary') }}
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="18" height="18">
              <path d="M5 12h14m-6-6 6 6-6 6" />
            </svg>
          </a>
          <a class="btn btn-ghost btn-lg" href="#how">{{ t('hero.ctaSecondary') }}</a>
        </div>
      </div>
    </section>

    <!-- Как работает -->
    <section id="how" class="section section-soft">
      <div class="container">
        <h2 class="section-title">{{ t('how.title') }}</h2>
        <div class="steps">
          <div v-for="s in steps" :key="s.n" class="step">
            <span class="step-n">{{ s.n }}</span>
            <h3>{{ s.title }}</h3>
            <p>{{ s.text }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Возможности -->
    <section id="features" class="section">
      <div class="container">
        <h2 class="section-title">{{ t('features.title') }}</h2>
        <div class="features">
          <div v-for="f in features" :key="f.title" class="feature">
            <span class="feature-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" width="22" height="22">
                <path :d="f.icon" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </span>
            <h3>{{ f.title }}</h3>
            <p>{{ f.text }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA-полоса -->
    <section class="cta-band">
      <div class="container cta-in">
        <h2>{{ t('cta.title') }}</h2>
        <p>{{ t('cta.text') }}</p>
        <a class="btn btn-white btn-lg" :href="LOGIN_URL">{{ t('cta.button') }}</a>
      </div>
    </section>

    <!-- Футер -->
    <footer class="footer">
      <div class="container footer-in">
        <span>© {{ new Date().getFullYear() }} Hubario</span>
        <span class="footer-muted">{{ t('footer.tagline') }}</span>
      </div>
    </footer>
  </div>
</template>

<style scoped>
.container {
  max-width: 1080px;
  margin: 0 auto;
  padding: 0 24px;
}

/* Шапка */
.header {
  position: sticky;
  top: 0;
  z-index: 10;
  background: color-mix(in srgb, var(--bg) 85%, transparent);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid var(--border);
}
.header-in {
  height: 64px;
  display: flex;
  align-items: center;
  gap: 32px;
}
.brand {
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
}
.brand-logo {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: linear-gradient(135deg, var(--brand-700), var(--brand-500));
  color: #fff;
  display: grid;
  place-items: center;
}
.brand-logo svg {
  width: 18px;
  height: 18px;
}
.brand-name {
  font-family: var(--font-head);
  font-weight: 800;
  font-size: 19px;
}
.nav {
  display: flex;
  gap: 24px;
  font-size: 14.5px;
  font-weight: 500;
  color: var(--text-muted);
}
.nav a {
  text-decoration: none;
}
.nav a:hover {
  color: var(--text);
}
.header-cta {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 10px;
}

/* Пилюля выбора языка (стиль от .btn-ghost, активный сегмент — primary) */
.lang-pill {
  display: inline-flex;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--bg);
  overflow: hidden;
}
.lang-pill__seg {
  padding: 8px 11px;
  border: none;
  background: transparent;
  color: var(--text-muted);
  font: inherit;
  font-size: 12.5px;
  font-weight: 700;
  letter-spacing: 0.04em;
  cursor: pointer;
  transition: 0.15s;
}
.lang-pill__seg:hover {
  background: var(--bg-soft);
  color: var(--text);
}
.lang-pill__seg.is-active {
  background: var(--brand-600);
  color: #fff;
}

/* Кнопки */
.btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 18px;
  border-radius: 10px;
  font-size: 14.5px;
  font-weight: 600;
  text-decoration: none;
  transition: 0.15s;
}
.btn-lg {
  padding: 13px 26px;
  font-size: 16px;
  border-radius: 12px;
}
.btn-primary {
  background: var(--brand-600);
  color: #fff;
}
.btn-primary:hover {
  background: var(--brand-700);
  box-shadow: 0 6px 18px color-mix(in srgb, var(--brand-600) 35%, transparent);
  transform: translateY(-1px);
}
.btn-ghost {
  color: var(--text);
  border: 1px solid var(--border);
  background: var(--bg);
}
.btn-ghost:hover {
  background: var(--bg-soft);
}
.btn-white {
  background: #fff;
  color: var(--brand-900);
}
.btn-white:hover {
  transform: translateY(-1px);
}

/* Hero */
.hero {
  padding: 96px 0 88px;
  background:
    radial-gradient(60% 80% at 70% 0%, color-mix(in srgb, var(--brand-500) 10%, transparent), transparent),
    var(--bg);
}
.hero-in {
  text-align: center;
  max-width: 760px;
}
.hero-badge {
  display: inline-block;
  padding: 6px 14px;
  border-radius: 999px;
  background: color-mix(in srgb, var(--brand-500) 10%, transparent);
  border: 1px solid color-mix(in srgb, var(--brand-500) 25%, transparent);
  color: var(--brand-700);
  font-size: 13.5px;
  font-weight: 600;
  margin-bottom: 22px;
}
.hero-title {
  font-size: 52px;
  font-weight: 800;
  letter-spacing: -0.02em;
}
.hero-grad {
  background: linear-gradient(100deg, var(--brand-600), var(--secondary) 55%, var(--accent));
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}
.hero-sub {
  margin: 20px auto 0;
  max-width: 560px;
  font-size: 18px;
  color: var(--text-muted);
}
.hero-cta {
  margin-top: 34px;
  display: flex;
  gap: 14px;
  justify-content: center;
}

/* Секции */
.section {
  padding: 80px 0;
}
.section-soft {
  background: var(--bg-soft);
  border-block: 1px solid var(--border);
}
.section-title {
  text-align: center;
  font-size: 32px;
  font-weight: 800;
  margin-bottom: 48px;
}

/* Шаги */
.steps {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
}
.step {
  background: var(--bg);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 28px;
  transition:
    transform 0.2s ease-out,
    box-shadow 0.2s ease-out,
    border-color 0.2s ease-out;
}
.step:hover {
  transform: translateY(-3px);
  border-color: color-mix(in srgb, var(--brand-500) 40%, var(--border));
  box-shadow: 0 12px 28px color-mix(in srgb, var(--brand-600) 10%, transparent);
}
.step-n {
  display: inline-grid;
  place-items: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: linear-gradient(135deg, var(--brand-600), var(--secondary));
  color: #fff;
  font-family: var(--font-head);
  font-weight: 800;
  margin-bottom: 16px;
}
.step h3 {
  font-size: 18px;
  margin-bottom: 8px;
}
.step p {
  margin: 0;
  color: var(--text-muted);
  font-size: 15px;
}

/* Возможности */
.features {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
}
.feature {
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 26px;
  transition:
    transform 0.2s ease-out,
    box-shadow 0.2s ease-out,
    border-color 0.2s ease-out;
}
.feature:hover {
  transform: translateY(-3px);
  border-color: color-mix(in srgb, var(--brand-500) 40%, var(--border));
  box-shadow: 0 12px 28px color-mix(in srgb, var(--brand-600) 10%, transparent);
}
.feature-icon {
  display: inline-grid;
  place-items: center;
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: color-mix(in srgb, var(--brand-500) 12%, transparent);
  color: var(--brand-600);
  margin-bottom: 14px;
}
.feature h3 {
  font-size: 17px;
  margin-bottom: 8px;
}
.feature p {
  margin: 0;
  color: var(--text-muted);
  font-size: 14.5px;
}

/* CTA-полоса */
.cta-band {
  background: linear-gradient(120deg, var(--brand-950), var(--brand-700) 60%, var(--secondary));
  color: #fff;
  padding: 72px 0;
}
.cta-in {
  text-align: center;
}
.cta-band h2 {
  font-size: 30px;
  font-weight: 800;
}
.cta-band p {
  margin: 12px 0 26px;
  opacity: 0.85;
}

/* Футер */
.footer {
  padding: 28px 0;
  border-top: 1px solid var(--border);
  font-size: 14px;
}
.footer-in {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
}
.footer-muted {
  color: var(--text-muted);
}

/* Планшет и уже */
@media (max-width: 900px) {
  .steps,
  .features {
    grid-template-columns: 1fr;
  }
  .hero-title {
    font-size: 38px;
  }
  .nav {
    display: none;
  }
}
</style>
